// Libs
import React, { useEffect, useState } from 'react';
import {
  useLazyQuery,
  NetworkStatus,
  useQuery,
  useMutation,
  useApolloClient
} from '@apollo/client';
import styled from 'styled-components';

// Components
import {
  BinderContent,
  Checkbox,
  Flexbox,
  TextInput
} from '@ibinder/common-frontend';
import { DateTimePicker } from '@ibinder/common-frontend/dist/dateAndTimePickers';
import SearchableDropDown from './SearchableDropDown';

// Utils
import {
  GET_ALL_BINDERS,
  GET_ISSUE_TEMPLATES,
  GET_ALL_INSPECTION_TEMPLATE,
  GET_ALL_ORGS,
  GET_FACILITY_STRUCTURES
} from '../../api/queries/queries_local';
import { adminDataSchema } from './FormValidation';
import { MetadataElementIdConstants } from '@ibinder/common-utils';
import useImpersonateAdmin, {
  impersonateHeader,
  impersonateAdminnHeader
} from '../../utils/useImpersonateAdmin';

// Icons
import {
  check as checkIcon,
  close,
  exclamationCircle
} from '@ibinder/common-icons';

import {
  FormContainer,
  StyledButton,
  Icon,
  StyledDocumentActionsSnackbar,
  StyledToolButton
} from './form.styles';
import { CREATE_INSPECTION_WORKFLOW } from '../../api/mutations/inspectionTemplate.query';

const Container = styled(Flexbox)`
  margin-bottom: 1rem;
`;

const getTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString();
};

const AdminForm = () => {
  const client = useApolloClient();

  const [appStatus, setAppStatus] = useState(null);
  const [responseMsg, setResponseMsg] = useState('');
  const [formCompleted, setFormCompleted] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [createTab, setCreateTab] = useState(true);
  const [tabName, setTabName] = useState('');
  const [bindersData, setBindersData] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [dateTime, setDateTime] = useState(getTomorrow());

  const [selectedType, setSelectedType] = useState({
    organization: null,
    binders: null,
    issueTemplate: null,
    inspectionTemplate: null
  });

  const orgs = useQuery(GET_ALL_ORGS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const [useAdminToken] = useImpersonateAdmin(
    client,
    orgs.data?.get[selectedType.organization]?.id
  );

  const [createInspectionWorkflow] = useMutation(CREATE_INSPECTION_WORKFLOW);

  const getSelectedValues = () => {
    return {
      organizationId: orgs.data.get[selectedType.organization].id,
      facilityId,
      binderId: bindersData[selectedType.binders].id,
      issueTemplateId: issueTemplates.data.get[selectedType.issueTemplate].id,
      inspectionTemplateId:
        inspectionTemplates.data.get.data[selectedType.inspectionTemplate].id,
      inspectionStart: dateTime,
      createCmTab: createTab,
      tabName
    };
  };

  const querySettings = (name, errorCb) => {
    const settings = {
      notifyOnNetworkStatusChange: true,
      onError: () => {
        setAppStatus('error');
        setResponseMsg((prevState) => {
          return `Status code ${errorCb()} - ${name}. ${
            !responseMsg.includes(name) ? `${prevState}` : ''
          } `;
        });
        setSelectedType({
          organization: null,
          binders: null,
          issueTemplate: null,
          inspectionTemplate: null
        });
      }
    };
    return settings;
  };

  const [getBinders, binders] = useLazyQuery(GET_ALL_BINDERS, {
    ...querySettings('Binders', () => {
      const base = binders.error.networkError.response;
      return `${base.status} ${base.statusText}`;
    }),
    fetchPolicy: 'network-only',
    onCompleted: (res) => setBindersData(res.get)
  });

  const { data: facilityStructures } = useQuery(GET_FACILITY_STRUCTURES, {
    variables: {
      facilityId: facilityId
    },
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      if (res.get.length <= 0) {
        setAppStatus('error');
        setResponseMsg('This binder does not have a facility structure.');
        setSelectedType((prevState) => ({
          ...prevState,
          binders: null,
          issueTemplate: null,
          inspectionTemplate: null
        }));
      } else {
        setAppStatus(null);
        setResponseMsg('');
      }
    },
    skip: !facilityId
  });

  const [getissueTemplates, issueTemplates] = useLazyQuery(
    GET_ISSUE_TEMPLATES,
    {
      ...querySettings('Remark Templates', () => {
        const base = issueTemplates.error.networkError.response;
        return `${base.status} ${base.statusText}`;
      }),
      fetchPolicy: 'no-cache'
    }
  );

  const facilityIdCheck =
    selectedType.binders !== null
      ? binders.data.get[selectedType.binders].metadata.filter(
          (i) =>
            i.metadataElementId === MetadataElementIdConstants.GLOBAL_FacilityId
        )
      : null;

  const [getInspectionTemplates, inspectionTemplates] = useLazyQuery(
    GET_ALL_INSPECTION_TEMPLATE,
    {
      ...querySettings('Inspection Templates', () => {
        const base = inspectionTemplates.error.networkError.response ?? '';
        return `${base.status} ${base.statusText}`;
      }),
      fetchPolicy: 'no-cache'
    }
  );

  useEffect(() => {
    // Reset other selections upon repicking an organization
    setFormCompleted(false);

    if (selectedType.binders !== null) {
      setSelectedType((prevState) => ({
        ...prevState,
        issueTemplate: null,
        inspectionTemplate: null
      }));
    }
  }, [selectedType.organization]);

  useEffect(() => {
    if (!useAdminToken) return;

    if (!binders.data) {
      getBinders(impersonateHeader(useAdminToken));
      getissueTemplates(impersonateHeader(useAdminToken));
      getInspectionTemplates(impersonateHeader(useAdminToken));
    } else {
      binders.refetch(impersonateHeader(useAdminToken));
      issueTemplates.refetch(impersonateHeader(useAdminToken));
      inspectionTemplates.refetch(impersonateHeader(useAdminToken));
      setAppStatus(null);
      setResponseMsg('');
    }
  }, [useAdminToken]);

  useEffect(() => {
    if (selectedType.binders !== null) {
      if (facilityIdCheck.length > 0) {
        setFacilityId(facilityIdCheck[0].valueId);
        setAppStatus(null);
        setResponseMsg('');
      } else {
        setAppStatus('error');
        setResponseMsg('This binder does not have a Facility ID.');
        setSelectedType((prevState) => ({
          ...prevState,
          binders: null,
          issueTemplate: null,
          inspectionTemplate: null
        }));
      }
    }
  }, [selectedType.binders]);

  useEffect(() => {
    if (
      selectedType.inspectionTemplate !== null &&
      selectedType.issueTemplate !== null
    )
      setFormCompleted(true);
  }, [selectedType.inspectionTemplate, selectedType.issueTemplate]);

  const postAdminData = () => {
    const adminData = getSelectedValues();

    const input = {
      variables: {
        adminData
      },
      ...impersonateAdminnHeader(useAdminToken)
    };

    createInspectionWorkflow(input).then(() => {
      {
        setPostLoading(false);
        setAppStatus('done');
        setResponseMsg('Success!');
        setSelectedType({
          organization: null,
          binders: null,
          issueTemplate: null,
          inspectionTemplate: null
        });
        setTabName('');
        setCreateTab(true);
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const adminData = getSelectedValues();

    adminDataSchema
      .validate(adminData, { abortEarly: false })
      .then(() => {
        setResponseMsg('');
        setPostLoading(true);
        postAdminData();
      })
      .catch((err) => {
        setPostLoading(false);
        setResponseMsg(err.message);
        setAppStatus('error');
        setSelectedType((prevState) => ({
          ...prevState,
          issueTemplate: null,
          inspectionTemplate: null
        }));
      });
  };

  return (
    <BinderContent loading={orgs.loading ? true : undefined}>
      <FormContainer onSubmit={handleSubmit}>
        {appStatus && (
          <StyledDocumentActionsSnackbar
            state={appStatus === 'done' ? appStatus : 'error'}
            animationEnabled={true}
            action={
              <StyledToolButton
                type="button"
                onClick={() => setAppStatus(null)}
              >
                <span>{close}</span>
              </StyledToolButton>
            }
          >
            <Flexbox alignItems="center">
              <Icon>
                {appStatus === 'done' ? checkIcon : exclamationCircle}
              </Icon>
              <span>{responseMsg}</span>
            </Flexbox>
          </StyledDocumentActionsSnackbar>
        )}

        {orgs && (
          <SearchableDropDown
            {...{ selectedType, setSelectedType }}
            label="Organization"
            data={orgs.data && orgs.data.get}
            loading={orgs.networkStatus !== NetworkStatus.ready}
            type="organization"
          />
        )}
        {binders.data && selectedType.organization !== null && (
          <SearchableDropDown
            {...{ selectedType, setSelectedType }}
            label="Binders"
            data={bindersData}
            loading={binders.networkStatus !== NetworkStatus.ready}
            type="binders"
          />
        )}
        {issueTemplates &&
          inspectionTemplates &&
          selectedType.binders !== null &&
          appStatus !== 'error' && (
            <>
              <SearchableDropDown
                {...{ selectedType, setSelectedType }}
                label="Issue Template"
                data={issueTemplates.data && issueTemplates.data.get}
                loading={binders.networkStatus !== NetworkStatus.ready}
                type="issueTemplate"
              />
              <SearchableDropDown
                {...{ selectedType, setSelectedType }}
                label="Inspection Template"
                data={
                  inspectionTemplates.data && inspectionTemplates.data.get.data
                }
                loading={binders.networkStatus !== NetworkStatus.ready}
                type="inspectionTemplate"
              />
              <Container justifyContent="space-around">
                <Checkbox
                  onChange={() => setCreateTab(!createTab)}
                  checked={!!createTab}
                  label="Create Tab?"
                />
                <TextInput
                  changeValue={(e) => setTabName(e.target.value)}
                  value={tabName}
                  type="text"
                  label="Tab Name"
                  disabled={!createTab}
                  required={createTab}
                />
              </Container>
            </>
          )}
        {selectedType.issueTemplate !== null &&
          selectedType.inspectionTemplate !== null && (
            <>
              <DateTimePicker
                defaultValue={dateTime}
                error={false}
                required={true}
                timePickerText="Select time"
                timePickerWithButton={true}
                datePickerText="Select date"
                datePickerWithButton={true}
                onDateTimeSelected={(dateTime) => setDateTime(dateTime)}
              />
            </>
          )}

        <StyledButton
          data-test="submit-btn"
          btnType="regular"
          btnStyle="primary"
          label="Submit"
          type="submit"
          disabled={!formCompleted}
        />
        {postLoading && <div>Sending data...</div>}
      </FormContainer>
    </BinderContent>
  );
};

export default AdminForm;
